import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';

import { CloseMobile, ProductFilterSVG } from '@/components/SvgComponents';
import { Button, CheckboxWithText } from '@/components/ui';
import { useWindowSize } from '@/hooks/index';
import {
  hooks as miniMarketHooks,
  actions as marketActions,
} from '@/domains/mini-market';

import styles from './ProductFilter.module.scss';

const ProductFilter = () => {
  const { t, lang } = useTranslation();
  const [windowsX] = useWindowSize();
  const router = useRouter();

  const { data: brands } = miniMarketHooks.useGetBrands({ lang });
  const { data: colors } = miniMarketHooks.useGetColors({ lang });
  const { data: storages } = miniMarketHooks.useGetStorages({ lang });

  const [mobileActive, setMobileActive] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedStorages, setSelectedStorages] = useState([]);

  const dispatch = useDispatch();

  const handleBrandClick = (name) => {
    setSelectedBrands((prev) =>
      prev.includes(name)
        ? prev.filter((brandName) => brandName !== name)
        : [...prev, name],
    );
  };

  const handleColorClick = (name) => {
    setSelectedColors((prev) =>
      prev.includes(name)
        ? prev.filter((colorName) => colorName !== name)
        : [...prev, name],
    );
  };

  const handleStorageClick = (name) => {
    setSelectedStorages((prev) =>
      prev.includes(name)
        ? prev.filter((storageName) => storageName !== name)
        : [...prev, name],
    );
  };

  const resetFilters = () => {
    setSelectedBrands([]);
    setSelectedColors([]);
    setSelectedStorages([]);
  };

  useEffect(() => {
    dispatch(marketActions.setFilterBrands(selectedBrands));
    dispatch(marketActions.setFilterColors(selectedColors));
    dispatch(marketActions.setFilterStorages(selectedStorages));
  }, [selectedBrands, selectedColors, selectedStorages]);

  useEffect(() => {
    const query = { ...router.query };

    if (selectedBrands.length > 0) {
      query.brands = selectedBrands.join(',');
    } else {
      delete query.brands;
    }

    if (selectedColors.length > 0) {
      query.colors = selectedColors.join(',');
    } else {
      delete query.colors;
    }

    if (selectedStorages.length > 0) {
      query.storages = selectedStorages.join(',');
    } else {
      delete query.storages;
    }

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true },
    );
  }, [selectedBrands, selectedColors, selectedStorages]);

  useEffect(() => {
    const { brands, colors, storages } = router.query;

    if (brands) {
      setSelectedBrands(brands.split(','));
    }

    if (colors) {
      setSelectedColors(colors.split(','));
    }

    if (storages) {
      setSelectedStorages(storages.split(','));
    }
  }, []);

  const renderColor = (item) => {
    const isActive = selectedColors.includes(item.title);
    return (
      <span
        key={item.id}
        className={classNames(styles.color_box, { [styles.active]: isActive })}
        style={{ background: item.hex_code }}
        onClick={() => handleColorClick(item.title)}
      />
    );
  };

  const renderList = (item) => {
    let selectedArray;
    let handleClick;

    if (item.type === 'brand') {
      selectedArray = selectedBrands;
      handleClick = handleBrandClick;
    } else if (item.type === 'storage') {
      selectedArray = selectedStorages;
      handleClick = handleStorageClick;
    } else {
      selectedArray = selectedColors;
      handleClick = handleColorClick;
    }

    const isActive = selectedArray.includes(item.name);
    return mobileActive ? (
      <span
        className={classNames(styles.label, { [styles.active]: isActive })}
        key={item.id}
        onClick={() => handleClick(item.name)}
      >
        {item.name}
        {isActive && (
          <span className={styles.icon}>
            <CloseMobile />
          </span>
        )}
      </span>
    ) : (
      <div key={item.id} className={styles.list}>
        <CheckboxWithText
          size='large'
          checked={isActive}
          onChange={() => handleClick(item.name)}
          className={styles.checkbox}
        >
          {item.name}
        </CheckboxWithText>
      </div>
    );
  };

  return (
    <div
      className={classNames([
        styles.product_filter__wrapper,
        { [styles.active]: mobileActive },
      ])}
      itemScope
      itemType='https://schema.org/Filter'
    >
      <div className={styles.content_cover}>
        <div className={styles.top}>
          <h5 className={styles.title}>{t('common:filter')}</h5>
          {mobileActive ? (
            <CloseMobile onClick={() => setMobileActive(!mobileActive)} />
          ) : (
            <ProductFilterSVG
              {...(windowsX <= 900 && { onClick: () => setMobileActive(true) })}
            />
          )}
        </div>
        <div className={styles.filter_box}>
          <h5 className={styles.title}>{t('device:brand')}</h5>
          {brands?.map((brand) => renderList({ ...brand, type: 'brand' }))}
        </div>
        <div className={styles.filter_box}>
          <h5 className={styles.title}>{t('device:color')}</h5>
          {colors?.map(renderColor)}
        </div>
        <div className={styles.filter_box}>
          <h5 className={styles.title}>{t('device:storage')}</h5>
          {storages?.map((storage) =>
            renderList({ ...storage, type: 'storage' }),
          )}
        </div>
      </div>
      {mobileActive && windowsX <= 900 && (
        <div className={styles.mobile_buttons}>
          <Button
            className={styles.cancleBtn}
            variant='transparent'
            onClick={() => {
              resetFilters();
              router.push(
                {
                  pathname: router.pathname,
                  query: {},
                },
                undefined,
                { shallow: true },
              );
            }}
          >
            {t('device:common.btnReset')}
          </Button>
          <Button
            className={styles.applyBtn}
            onClick={() => setMobileActive(!mobileActive)}
          >
            {t('device:common.btnApply')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductFilter;
